(ns ui.editors.simple-filters.event
  (:require
   [clojure.string :as string]
   [common.opensearch.facet-policy :as facet-policy]
   [common.schema.data-types :as data-types]
   [re-frame.core :as rf]
   [ui.editors.simple-filters.model :as model]))

(defn- normalize-field-path
  [field-path]
  (cond
    (vector? field-path) field-path
    (keyword? field-path) [field-path]
    (sequential? field-path) (vec field-path)
    :else nil))

(defn- runtime-path
  [editor-id]
  (model/editor-path editor-id))

(defn- callback-fx
  [runtime callback-key payload]
  (when-let [event-v (get-in runtime [model/callbacks-key callback-key])]
    [[:dispatch (conj (vec event-v) payload)]]))

(defn- append-callback-fx
  [fx runtime callback-key payload]
  (if-let [cb (callback-fx runtime callback-key payload)]
    (into (vec fx) cb)
    (vec fx)))

(defn- changed-fx
  [db editor-id]
  (let [runtime (get-in db (runtime-path editor-id))
        by-field-path (get-in runtime [model/filters-key :by-field-path] {})]
    (append-callback-fx [] runtime :on-changed-event
                        {:editor-id editor-id
                         :filters-by-field-path by-field-path})))

(defn- field-filter-type
  [field]
  (let [data-type (:field/data-type field)]
    (or (:field/filter-type field)
        (get-in data-types/zapflow-data-types [data-type :default-filter]))))

(defn- facet-fetch-enabled-field?
  [field]
  (let [data-type (:field/data-type field)
        filter-type (field-filter-type field)]
    (and (some? field)
         (facet-policy/should-fetch-facets? {:data-type data-type
                                             :filter-type filter-type}))))

(defn- readable-field?
  [field]
  (let [explicit-readable? (contains? field :readable?)
        explicit-read-access? (contains? field :read-access?)]
    (cond
      explicit-readable? (boolean (:readable? field))
      explicit-read-access? (boolean (:read-access? field))
      :else true)))

(defn- index-fields-by-path
  [fields]
  (->> (or fields [])
       (keep (fn [{:keys [field-path] :as field}]
               (when-let [normalized-field-path (normalize-field-path field-path)]
                 [normalized-field-path
                  (assoc field :field-path normalized-field-path)])))
       (into {})))

(defn- field-schema->field
  [[attribute field-schema]]
  (let [filter-kind (get-in field-schema [:field/default-visibility :filter])
        hidden? (true? (:field/hidden-from-configuration? field-schema))
        field-path [attribute]]
    (when (and (keyword? attribute)
               (not hidden?)
               (not= filter-kind :filter.type/none)
               (not= filter-kind :editor.type/none))
      {:field-path field-path
       :field/attribute attribute
       :field/name (str attribute)
       :field/label (or (get-in field-schema [:field/translations :en])
                        (name attribute))
       :field/data-type (:field/data-type field-schema)
       :field/filter-type filter-kind
       :field/options (or (:options field-schema) [])
       :field/default-ord (or (:field/default-ord field-schema) 0)
       :readable? true
       :field/schema field-schema})))

(defn- resolve-fields
  [{:keys [fields field-schemas]}]
  (if (seq fields)
    (->> fields
         (map (fn [{:keys [field-path] :as field}]
                (if-let [normalized-field-path (normalize-field-path field-path)]
                  (assoc field :field-path normalized-field-path)
                  field)))
         vec)
    (->> (or field-schemas {})
         (map field-schema->field)
         (remove nil?)
         (sort-by (fn [field]
                    (or (:field/default-ord field) 0)))
         vec)))

(defn- sanitize-field-paths
  [field-paths allowed-field-paths]
  (let [allowed-set (set allowed-field-paths)]
    (->> (or field-paths [])
         (filter (fn [field-path]
                   (contains? allowed-set field-path)))
         distinct
         vec)))

(defn- drop-duplicates-from
  [field-paths excluded-field-paths]
  (let [excluded-set (set excluded-field-paths)]
    (->> (or field-paths [])
         (remove (fn [field-path]
                   (contains? excluded-set field-path)))
         vec)))

(defn- sort-field-paths-by-label
  [field-paths field-by-path]
  (->> (or field-paths [])
       (sort-by (fn [field-path]
                  (let [field (get field-by-path field-path)
                        label (or (:field/label field)
                                  (:field/name field)
                                  (:label field)
                                  (some-> field-path last name)
                                  "")]
                    (string/lower-case label))))
       vec))

(defn- sync-sections
  [runtime]
  (let [fields (get-in runtime [model/fields-key :ordered-paths] [])
        field-by-path (get-in runtime [model/fields-key :by-path] {})
        allowed-field-paths (->> fields
                                 (filter (fn [field-path]
                                           (readable-field? (get field-by-path field-path))))
                                 vec)
        po-configured (get-in runtime [model/po-key :configured-field-paths] [])
        po-visible (-> (sanitize-field-paths po-configured allowed-field-paths)
                       (sort-field-paths-by-label field-by-path))
        pinned (get-in runtime [model/pinned-key :field-paths] [])
        pinned-visible (-> pinned
                           (sanitize-field-paths allowed-field-paths)
                           (drop-duplicates-from po-visible)
                           (sort-field-paths-by-label field-by-path))
        runtime-paths (get-in runtime [model/sections-key :runtime-field-paths] [])
        runtime-visible (-> runtime-paths
                            (sanitize-field-paths allowed-field-paths)
                            (drop-duplicates-from po-visible)
                            (drop-duplicates-from pinned-visible)
                            (sort-field-paths-by-label field-by-path))
        active-field-set (set (concat po-visible pinned-visible runtime-visible))
        filters-by-field-path (get-in runtime [model/filters-key :by-field-path] {})
        filtered-filters-by-field-path (->> filters-by-field-path
                                            (filter (fn [[field-path _]]
                                                      (contains? active-field-set field-path)))
                                            (into {}))]
    (-> runtime
        (assoc-in [model/po-key :visible-field-paths] po-visible)
        (assoc-in [model/pinned-key :field-paths] pinned-visible)
        (assoc-in [model/sections-key :po-field-paths] po-visible)
        (assoc-in [model/sections-key :pinned-field-paths] pinned-visible)
        (assoc-in [model/sections-key :runtime-field-paths] runtime-visible)
        (assoc-in [model/filters-key :by-field-path] filtered-filters-by-field-path))))

(defn- set-runtime
  [db editor-id runtime]
  (assoc-in db (runtime-path editor-id) runtime))

(defn- update-runtime
  [db editor-id f & args]
  (apply update-in db (runtime-path editor-id) f args))

(defn- normalize-facet-option
  [option]
  (let [value (cond
                (contains? option :value) (:value option)
                (contains? option :key) (:key option)
                (contains? option :keyword) (:keyword option)
                :else nil)]
    (when (some? value)
      {:value value
       :label (or (:label option)
                  (when (keyword? value)
                    (name value))
                  (str value))
       :count (:count option)
       :from (:from option)
       :to (:to option)})))

(def default-facets-page-size 50)

(defn- merge-facet-options
  [existing incoming]
  (let [normalize-key (fn [option]
                        (let [value (:value option)]
                          (cond
                            (keyword? value) (str value)
                            :else (str value))))]
    (->> (concat (or existing []) (or incoming []))
         (reduce (fn [acc option]
                   (let [k (normalize-key option)]
                     (assoc acc k option)))
                 {})
         (vals)
         vec)))

(defn- facet-option-key
  [value]
  (cond
    (keyword? value) (str value)
    :else (str value)))

(def known-option-value :filter.option/known)
(def unknown-option-value :filter.option/unknown)
(def existence-option-values #{known-option-value unknown-option-value})

(defn- preserve-existence-option-counts
  [existing-options incoming-options search-text]
  (if (string/blank? search-text)
    incoming-options
    (let [existing-counts-by-value (->> (or existing-options [])
                                        (filter (fn [{:keys [value]}]
                                                  (contains? existence-option-values value)))
                                        (map (fn [{:keys [value count]}]
                                               [value count]))
                                        (into {}))]
      (->> (or incoming-options [])
           (map (fn [{:keys [value] :as option}]
                  (if (contains? existence-option-values value)
                    (if-let [existing-count (get existing-counts-by-value value)]
                      (assoc option :count existing-count)
                      option)
                    option)))
           vec))))

(defn- sort-facet-options
  "Selected options first, then the largest count. The label comes last. It is
   compared without regard to case, and then as it is, so that the order is the
   same each time."
  [options selected-values]
  (let [selected-keys (->> (or selected-values [])
                           (map facet-option-key)
                           set)]
    (->> (or options [])
         (sort-by (fn [{:keys [count label value]}]
                    (let [label-text (str (or label
                                              (when (keyword? value)
                                                (name value))
                                              value))]
                      [(if (contains? selected-keys (facet-option-key value)) 0 1)
                       (- (or count 0))
                       (string/lower-case label-text)
                       label-text])))
         vec)))

(defn- maybe-request-facets
  [db editor-id field-path search-text {:keys [append?]
                                        :or {append? false}}]
  (let [runtime (get-in db (runtime-path editor-id))
        load-facets-event (get-in runtime [model/callbacks-key :load-facets-event])
        field (get-in runtime [model/fields-key :by-path field-path])
        runtime-fields-by-path (get-in runtime [model/fields-key :by-path] {})
        runtime-accessible-fields (->> runtime-fields-by-path vals vec)
        field-data-types-by-path (->> runtime-fields-by-path
                                      (map (fn [[path f]]
                                             [path (:field/data-type f)]))
                                      (into {}))
        callback-context (assoc (or (get runtime model/context-key) {})
                                :accessible-fields runtime-accessible-fields)
        search-text (or search-text "")
        loading? (true? (get-in runtime [model/facets-key :loading-by-field-path field-path]))
        has-more? (true? (get-in runtime [model/facets-key :has-more-by-field-path field-path]))
        next-offset (or (get-in runtime [model/facets-key :next-offset-by-field-path field-path])
                        (count (get-in runtime [model/facets-key :by-field-path field-path] [])))
        request-offset (if append? next-offset 0)
        has-callback? (and (vector? load-facets-event)
                           (seq load-facets-event))
        has-field-path? (some? field-path)
        selectable-field? (facet-fetch-enabled-field? field)
        not-loading? (not loading?)
        pagination-ok? (or (not append?) has-more?)
        allowed-request? (and (vector? load-facets-event)
                              (seq load-facets-event)
                              field-path
                              (facet-fetch-enabled-field? field)
                              (not loading?)
                              (or (not append?) has-more?))]
    (js/console.log "[simple-filters] maybe-request-facets"
                    #js {:editorId editor-id
                         :fieldPath (pr-str field-path)
                         :append append?
                         :searchText search-text
                         :hasCallback has-callback?
                         :hasFieldPath has-field-path?
                         :selectableField selectable-field?
                         :notLoading not-loading?
                         :hasMore has-more?
                         :paginationOk pagination-ok?
                         :allowedRequest allowed-request?
                         :loadFacetsEvent (pr-str load-facets-event)
                         :fieldFilterType (some-> field :field/filter-type str)
                         :fieldDataType (some-> field :field/data-type str)
                         :requestOffset request-offset})
    (js/console.warn "[simple-filters] maybe-request-facets"
                     #js {:editorId editor-id
                          :fieldPath (pr-str field-path)
                          :allowedRequest allowed-request?
                          :loadFacetsEvent (pr-str load-facets-event)
                          :fieldFilterType (some-> field :field/filter-type str)
                          :fieldDataType (some-> field :field/data-type str)})
    (if allowed-request?
      {:db (-> db
               (assoc-in (conj (model/facets-path editor-id) :loading-by-field-path field-path) true)
               (cond-> (not append?)
                 (assoc-in (conj (model/facets-path editor-id) :next-offset-by-field-path field-path) 0))
               (update-in (conj (model/facets-path editor-id) :error-by-field-path) dissoc field-path))
       :fx [[:dispatch
             (conj load-facets-event
                   {:editor-id editor-id
                    :field-path field-path
                    :field-data-type (:field/data-type field)
                    :field-filter-type (:field/filter-type field)
                    :filters-by-field-path (get-in runtime [model/filters-key :by-field-path])
                    :search-text search-text
                    :offset request-offset
                    :limit default-facets-page-size
                    :append? append?
                    :context callback-context
                    :field-data-types-by-path field-data-types-by-path
                    :preference-context (get runtime model/preference-context-key)})]]}
      {:db db})))

(defn- selection->id
  [selection]
  (or (:id selection)
      (pr-str (dissoc selection :id))))

(defn- upsert-selection*
  [selections selection]
  (let [target-id (selection->id selection)
        normalized (assoc selection :id target-id)]
    (->> (or selections [])
         (remove (fn [existing]
                   (= (selection->id existing) target-id)))
         (cons normalized)
         vec)))

(rf/reg-event-db
 ::initialize
 (fn [db [_ {:keys [editor-id
                    view-id
                    entity-type
                    pipeline-id
                    account-id
                    fields
                    field-schemas
                    po-configured-field-paths
                    pinned-field-paths
                    runtime-field-paths
                    filters-by-field-path
                    load-facets-event
                    on-changed-event
                    facets-by-field-path
                    loading-facets-by-field-path
                    facets-error-by-field-path
                    facets-has-more-by-field-path
                    facets-next-offset-by-field-path]}]]
   (let [resolved-fields (resolve-fields {:fields fields
                                          :field-schemas field-schemas})
         runtime (model/default-runtime {:editor-id editor-id
                                         :view-id view-id
                                         :entity-type entity-type
                                         :pipeline-id pipeline-id
                                         :account-id account-id})
         runtime (-> runtime
                     (assoc-in [model/fields-key :ordered-paths]
                               (->> (or resolved-fields [])
                                    (keep :field-path)
                                    distinct
                                    vec))
                     (assoc-in [model/fields-key :by-path] (index-fields-by-path resolved-fields))
                     (assoc-in [model/fields-key :status] :ready)
                     (assoc-in [model/po-key :configured-field-paths] (vec (or po-configured-field-paths [])))
                     (assoc-in [model/pinned-key :field-paths] (vec (or pinned-field-paths [])))
                     (assoc-in [model/sections-key :runtime-field-paths] (vec (or runtime-field-paths [])))
                     (assoc-in [model/filters-key :by-field-path] (or filters-by-field-path {}))
                     (assoc-in [model/facets-key :by-field-path]
                               (->> (or facets-by-field-path {})
                                    (map (fn [[field-path options]]
                                           [field-path
                                            (->> (or options [])
                                                 (map normalize-facet-option)
                                                 (remove nil?)
                                                 vec)]))
                                    (into {})))
                     (assoc-in [model/facets-key :baseline-by-field-path]
                               (->> (or facets-by-field-path {})
                                    (map (fn [[field-path options]]
                                           [field-path
                                            (->> (or options [])
                                                 (map normalize-facet-option)
                                                 (remove nil?)
                                                 vec)]))
                                    (into {})))
                     (assoc-in [model/facets-key :loading-by-field-path] (or loading-facets-by-field-path {}))
                     (assoc-in [model/facets-key :error-by-field-path] (or facets-error-by-field-path {}))
                     (assoc-in [model/facets-key :has-more-by-field-path] (or facets-has-more-by-field-path {}))
                     (assoc-in [model/facets-key :next-offset-by-field-path] (or facets-next-offset-by-field-path {}))
                     (assoc-in [model/callbacks-key :load-facets-event] load-facets-event)
                     (assoc-in [model/callbacks-key :on-changed-event] on-changed-event)
                     (sync-sections))]
     (set-runtime db editor-id runtime))))

(rf/reg-event-db
 ::destroy
 (fn [db [_ editor-id]]
   (update-in db model/root-path dissoc editor-id)))

(rf/reg-event-db
 ::set-context
 (fn [db [_ editor-id context]]
   (assoc-in db (model/context-path editor-id) (or context {}))))

(rf/reg-event-db
 ::set-preference-context
 (fn [db [_ editor-id preference-context]]
   (assoc-in db (model/preference-context-path editor-id) (or preference-context {}))))

(rf/reg-event-db
 ::set-fields
 (fn [db [_ editor-id {:keys [fields field-schemas status error]}]]
   (let [resolved-fields (resolve-fields {:fields fields
                                          :field-schemas field-schemas})
         db (-> db
                (assoc-in (conj (model/fields-path editor-id) :ordered-paths)
                          (->> (or resolved-fields [])
                               (keep :field-path)
                               distinct
                               vec))
                (assoc-in (conj (model/fields-path editor-id) :by-path) (index-fields-by-path resolved-fields))
                (cond-> status
                  (assoc-in (conj (model/fields-path editor-id) :status) status))
                (assoc-in (conj (model/fields-path editor-id) :error) error))]
     (update-runtime db editor-id sync-sections))))

(rf/reg-event-db
 ::set-fields-search-text
 (fn [db [_ editor-id search-text]]
   (assoc-in db (conj (model/fields-path editor-id) :search-text) (or search-text ""))))

(rf/reg-event-db
 ::set-po-configured-field-paths
 (fn [db [_ editor-id field-paths]]
   (-> db
       (assoc-in (conj (model/po-path editor-id) :configured-field-paths) (vec (or field-paths [])))
       (update-runtime editor-id sync-sections))))

(rf/reg-event-db
 ::set-pinned-field-paths
 (fn [db [_ editor-id field-paths]]
   (-> db
       (assoc-in (conj (model/pinned-path editor-id) :field-paths) (vec (or field-paths [])))
       (update-runtime editor-id sync-sections))))

(rf/reg-event-db
 ::toggle-pinned-field-path
 (fn [db [_ editor-id field-path]]
   (let [current (get-in db (conj (model/pinned-path editor-id) :field-paths) [])
         pinned? (some #(= % field-path) current)
         next-paths (if pinned?
                      (->> current
                           (remove #(= % field-path))
                           vec)
                      (-> current
                          vec
                          (conj field-path)
                          distinct
                          vec))]
     (-> db
         (assoc-in (conj (model/pinned-path editor-id) :field-paths) next-paths)
         (update-runtime editor-id sync-sections)))))

(rf/reg-event-db
 ::set-runtime-field-paths
 (fn [db [_ editor-id field-paths]]
   (-> db
       (assoc-in (conj (model/sections-path editor-id) :runtime-field-paths) (vec (or field-paths [])))
       (update-runtime editor-id sync-sections))))

(rf/reg-event-db
 ::add-runtime-field-path
 (fn [db [_ editor-id field-path]]
   (let [current (get-in db (conj (model/sections-path editor-id) :runtime-field-paths) [])
         next-paths (-> current
                        vec
                        (conj field-path)
                        distinct
                        vec)]
     (-> db
         (assoc-in (conj (model/sections-path editor-id) :runtime-field-paths) next-paths)
         (update-runtime editor-id sync-sections)))))

(rf/reg-event-db
 ::remove-runtime-field-path
 (fn [db [_ editor-id field-path]]
   (let [current (get-in db (conj (model/sections-path editor-id) :runtime-field-paths) [])
         next-paths (->> current
                         (remove #(= % field-path))
                         vec)]
     (-> db
         (assoc-in (conj (model/sections-path editor-id) :runtime-field-paths) next-paths)
         (update-runtime editor-id sync-sections)))))

(rf/reg-event-db
 ::set-open-field-path
 (fn [db [_ editor-id field-path]]
   (assoc-in db (conj (model/ui-path editor-id) :open-field-path) field-path)))

(rf/reg-event-fx
 ::open-field-path
 (fn [{:keys [db]} [_ editor-id field-path]]
   (let [db (assoc-in db (conj (model/ui-path editor-id) :open-field-path) field-path)
         search-text (get-in db (conj (model/ui-path editor-id) :option-search-text-by-field field-path) "")]
     (js/console.log "[simple-filters] open-field-path"
                     #js {:editorId editor-id
                          :fieldPath (pr-str field-path)
                          :searchText search-text})
     (maybe-request-facets db editor-id field-path search-text {:append? false}))))

(rf/reg-event-db
 ::set-field-picker-open
 (fn [db [_ editor-id open?]]
   (assoc-in db (conj (model/ui-path editor-id) :field-picker-open?) (boolean open?))))

(rf/reg-event-db
 ::set-field-picker-search-text
 (fn [db [_ editor-id search-text]]
   (assoc-in db (conj (model/ui-path editor-id) :field-picker-search-text) (or search-text ""))))

(rf/reg-event-fx
 ::set-option-search-text
 (fn [{:keys [db]} [_ editor-id field-path search-text]]
   (let [search-text (or search-text "")
         db (assoc-in db
                      (conj (model/ui-path editor-id) :option-search-text-by-field field-path)
                      search-text)]
     (maybe-request-facets db editor-id field-path search-text {:append? false}))))

(rf/reg-event-fx
 ::load-more-facets
 (fn [{:keys [db]} [_ editor-id field-path]]
   (let [search-text (get-in db (conj (model/ui-path editor-id) :option-search-text-by-field field-path) "")]
     (maybe-request-facets db editor-id field-path search-text {:append? true}))))

(rf/reg-event-db
 ::clear-option-search-text
 (fn [db [_ editor-id field-path]]
   (update-in db (conj (model/ui-path editor-id) :option-search-text-by-field) dissoc field-path)))

(rf/reg-event-fx
 ::clear-field-selection
 (fn [{:keys [db]} [_ editor-id field-path]]
   (let [db (-> db
                (update-in (conj (model/ui-path editor-id) :option-search-text-by-field) dissoc field-path)
                (update-in (conj (model/filters-path editor-id) :by-field-path) dissoc field-path))]
     (maybe-request-facets db editor-id field-path "" {:append? false}))))

(rf/reg-event-db
 ::set-load-facets-event
 (fn [db [_ editor-id load-facets-event]]
   (assoc-in db (conj (model/callbacks-path editor-id) :load-facets-event) load-facets-event)))

(rf/reg-event-db
 ::set-field-facets-loading
 (fn [db [_ editor-id field-path loading?]]
   (assoc-in db (conj (model/facets-path editor-id) :loading-by-field-path field-path) (boolean loading?))))

(rf/reg-event-db
 ::set-field-facets-error
 (fn [db [_ editor-id field-path error]]
   (-> db
       (assoc-in (conj (model/facets-path editor-id) :loading-by-field-path field-path) false)
       (assoc-in (conj (model/facets-path editor-id) :error-by-field-path field-path) error))))

(rf/reg-event-db
 ::set-field-facets
 (fn [db [_ editor-id field-path options-or-response]]
   (let [{:keys [options append? has-more next-offset]}
         (if (map? options-or-response)
           {:options (or (:options options-or-response) [])
            :append? (boolean (:append? options-or-response))
            :has-more (boolean (:has-more options-or-response))
            :next-offset (:next-offset options-or-response)}
           {:options (or options-or-response [])
            :append? false
            :has-more false
            :next-offset nil})
         normalized-options (->> options
                                 (map normalize-facet-option)
                                 (remove nil?)
                                 vec)
         existing-options (get-in db (conj (model/facets-path editor-id) :by-field-path field-path) [])
         baseline-options (get-in db (conj (model/facets-path editor-id) :baseline-by-field-path field-path) [])
         search-text (get-in db (conj (model/ui-path editor-id) :option-search-text-by-field field-path) "")
         normalized-options (preserve-existence-option-counts (or (seq baseline-options) existing-options)
                                                              normalized-options
                                                              search-text)
         selected-values (->> (get-in db (conj (model/filters-path editor-id) :by-field-path field-path :selections) [])
                              (map :value)
                              (remove nil?)
                              vec)
         merged-options (if append?
                          (merge-facet-options existing-options normalized-options)
                          normalized-options)
         merged-options (sort-facet-options merged-options selected-values)
         computed-next-offset (or next-offset
                                  (if append?
                                    (count merged-options)
                                    (count normalized-options)))]
     (-> db
         (assoc-in (conj (model/facets-path editor-id) :by-field-path field-path) merged-options)
         (cond-> (and (string/blank? search-text) (not append?))
           (assoc-in (conj (model/facets-path editor-id) :baseline-by-field-path field-path) merged-options))
         (assoc-in (conj (model/facets-path editor-id) :has-more-by-field-path field-path) (boolean has-more))
         (assoc-in (conj (model/facets-path editor-id) :next-offset-by-field-path field-path) computed-next-offset)
         (assoc-in (conj (model/facets-path editor-id) :loading-by-field-path field-path) false)
         (update-in (conj (model/facets-path editor-id) :error-by-field-path) dissoc field-path)))))

(rf/reg-event-db
 ::set-busy
 (fn [db [_ editor-id busy-key busy?]]
   (assoc-in db (conj (model/ui-path editor-id) :busy-by-key busy-key) (boolean busy?))))

(rf/reg-event-db
 ::clear-busy
 (fn [db [_ editor-id busy-key]]
   (update-in db (conj (model/ui-path editor-id) :busy-by-key) dissoc busy-key)))

(defn set-field-filter-fx
  "Handler for `::set-field-filter`. Return no effect when the field already has
   exactly this filter. The input of a filter applies its value when the user
   presses Enter, pauses typing or leaves the input, so the same value can
   arrive more than once. Each change notification starts a new query."
  [{:keys [db]} [_ editor-id field-path field-filter]]
  (let [db' (if (nil? field-filter)
              (update-in db (conj (model/filters-path editor-id) :by-field-path) dissoc field-path)
              (assoc-in db
                        (conj (model/filters-path editor-id) :by-field-path field-path)
                        (merge {:field-path field-path
                                :mode :multi
                                :selections []}
                               field-filter)))]
    (if (= db db')
      {}
      {:db db'
       :fx (changed-fx db' editor-id)})))

(rf/reg-event-fx
 ::set-field-filter
 set-field-filter-fx)

(rf/reg-event-fx
 ::clear-field-filter
 (fn [{:keys [db]} [_ editor-id field-path]]
   (let [db' (update-in db (conj (model/filters-path editor-id) :by-field-path) dissoc field-path)]
     {:db db'
      :fx (changed-fx db' editor-id)})))

(rf/reg-event-fx
 ::clear-all-filters
 (fn [{:keys [db]} [_ editor-id]]
   (let [db' (assoc-in db (conj (model/filters-path editor-id) :by-field-path) {})]
     {:db db'
      :fx (changed-fx db' editor-id)})))

(rf/reg-event-fx
 ::set-all-filters
 (fn [{:keys [db]} [_ editor-id by-field-path]]
   (let [db' (assoc-in db (conj (model/filters-path editor-id) :by-field-path) (or by-field-path {}))]
     {:db db'
      :fx (changed-fx db' editor-id)})))

(rf/reg-event-fx
 ::upsert-field-selection
 (fn [{:keys [db]} [_ editor-id field-path selection]]
   (let [base (get-in db (conj (model/filters-path editor-id) :by-field-path field-path))
         current-selections (:selections base)
         next-selections (upsert-selection* current-selections selection)
         db' (assoc-in db
                       (conj (model/filters-path editor-id) :by-field-path field-path)
                       (merge {:field-path field-path
                               :mode :multi}
                              base
                              {:selections next-selections}))]
     {:db db'
      :fx (changed-fx db' editor-id)})))

(rf/reg-event-fx
 ::remove-field-selection
 (fn [{:keys [db]} [_ editor-id field-path target-selection-id]]
   (let [base (get-in db (conj (model/filters-path editor-id) :by-field-path field-path))
         current-selections (or (:selections base) [])
         next-selections (->> current-selections
                              (remove (fn [selection]
                                        (= (selection->id selection) target-selection-id)))
                              vec)
         db' (if (empty? next-selections)
               (update-in db (conj (model/filters-path editor-id) :by-field-path) dissoc field-path)
               (assoc-in db
                         (conj (model/filters-path editor-id) :by-field-path field-path :selections)
                         next-selections))]
     {:db db'
      :fx (changed-fx db' editor-id)})))
